/** **Publish now / Unpublish now** (plan O1, §5 "Header", §9.8): view mode's one Published
 *  control — in edit mode `published` is a draft field (Visibility & search) and the only
 *  control, so the page offers this item in view mode only. One step: a confirmation naming
 *  the consequences (`publishConsequences`), then `POST …/publish/` or `…/unpublish/`, then the
 *  receipt with the audit row and the server's consequences.
 *
 *  - Offered to `console_itineraries_manage`; disabled with the reason while editing is
 *    switched off on the environment (the server's kill switch refuses it too) or the itinerary
 *    has no operator. A placeholder's header keeps it disabled with its lock
 *    (`placeholderActionGroups`): the server refuses it.
 *  - "… now" never runs over unsaved changes: while the editor keeps a draft (view mode, after
 *    Close editor or a restore) it stays disabled, saying how to free it (`unsavedFirst`).
 *  - Publishing never creates a missing catalog itinerary: the app then shows "Coming soon",
 *    and the confirmation says so before anything happens.
 *  - Refusals, nothing changed: already published / unpublished (someone did it meanwhile — the
 *    page refetches and shows it); someone else saving the itinerary (Try again); editing
 *    switched off; the server's own words otherwise. */
import { useState, type ReactNode } from 'react';

import type { ItineraryDetail } from '@/api/itineraries';
import {
  itineraryActionRefusal,
  useSetItineraryPublished,
  type ItineraryPublishResult,
} from '@/api/itineraryActions';
import { CATALOG_EDITING_OFF } from '@/features/catalog/editGate';
import { joinAnd, liveVerdict } from '@/features/catalog/words';
import type { RecordActionItem } from '@/features/records/page/RecordActionsMenu';
import { auditHref, changesHref } from '@/utils/gridLinks';
import { formatZoned } from '@/utils/zonedTime';

import { ITINERARY_TIMEZONE } from '../editorConfig';
import { liveFacts } from '../parts/itineraryFactBandModel';
import { ITINERARY_ACTION, itineraryTitle } from '../parts/ItineraryRecordHeader';

import { ItineraryNowPopover, type ItineraryNowReceipt, type ItineraryNowRefusal } from './ItineraryNowPopover';

export const PUBLISH_NOW = 'Publish now';
export const UNPUBLISH_NOW = 'Unpublish now';

/** The app's refresh after a publish — the server's consequence, word for word. */
export const APP_REFRESH = 'The app’s catalog entry refreshes in ~3 s.';

/** What publishing (`to` true) or unpublishing does, before it happens — the server's
 *  consequences as the detail predicts them: where the app shows the itinerary then (its
 *  status sentence, "Coming soon" without a catalog itinerary — publishing does not create
 *  one), the app's refresh, the catalog itinerary other itineraries share (marked as well; their
 *  own Published setting unchanged) and, publishing one without a deep link, Branch.io making
 *  it. */
export function publishConsequences(
  d: Pick<
    ItineraryDetail,
    | 'id'
    | 'published'
    | 'placeholder'
    | 'default_itinerary'
    | 'show_in_catalog'
    | 'show_in_region_search'
    | 'show_in_trip_type_search'
    | 'show_in_text_search'
    | 'branchio_link'
  >,
  to: boolean,
): string[] {
  const lines = [liveVerdict({ ...liveFacts(d), published: to }).text, APP_REFRESH];
  const others = [...new Set(d.default_itinerary.shared_with)].filter((g) => g !== d.id);
  if (d.default_itinerary.id !== null && others.length > 0) {
    const refs = joinAnd(others.map((g) => `#${g}`));
    lines.push(
      `Its catalog itinerary, shared with ${others.length === 1 ? 'itinerary' : 'itineraries'} ${refs}, is marked ` +
        `${to ? 'published' : 'not published'} as well (their own Published setting is unchanged).`,
    );
  }
  if (to && !d.branchio_link?.trim()) lines.push('It has no deep link yet: Branch.io makes one.');
  return lines;
}

/** Why a "… now" waits while the editor keeps unsaved changes: "Save or discard your 2
 *  unsaved changes first"; null without any. */
export function unsavedFirst(count: number): string | null {
  return count > 0 ? `Save or discard your ${count} unsaved ${count === 1 ? 'change' : 'changes'} first` : null;
}

/** Why Publish now / Unpublish now did nothing, in the operator's terms. */
export function publishRefusal(e: unknown, to: boolean): ItineraryNowRefusal {
  const r = itineraryActionRefusal(e);
  switch (r.kind) {
    case 'same_state':
      return {
        text: `it is already ${to ? 'published' : 'not published'} — someone changed it meanwhile. The page shows it now.`,
        retry: false,
      };
    case 'locked':
      return { text: 'someone else is saving this itinerary right now — try again.', retry: true };
    case 'disabled':
      return { text: `${CATALOG_EDITING_OFF.toLowerCase()} on this environment.`, retry: false };
    default:
      return { text: r.detail, retry: r.kind === 'failed' };
  }
}

/** The receipt: "Published 8 Oct 11:02 UTC · audit #99303", the server's consequences, the
 *  audit row and the change history. */
export function publishReceipt(r: ItineraryPublishResult): ItineraryNowReceipt {
  return {
    headline: `${r.published ? 'Published' : 'Unpublished'} ${formatZoned(r.saved_ts, ITINERARY_TIMEZONE, { weekday: false })} · audit #${r.audit_id}`,
    lines: r.consequences,
    links: [
      { label: 'Audit row', to: auditHref({ task_id: r.audit_uuid }) },
      { label: 'Change history', to: changesHref({ task_id: r.audit_uuid }) },
    ],
  };
}

/** Publish now / Unpublish now for the page: the Actions item (the one that applies) and the
 *  popover it opens. `ask` opens it from elsewhere (the delete refusal's Unpublish), on the
 *  element given. `blockedReason`: why it is offered disabled (editing switched off, no
 *  operator). */
export function usePublishNow(
  detail: ItineraryDetail,
  { blockedReason }: { blockedReason: string | null },
): { item: RecordActionItem; ask: (anchorEl: HTMLElement | null) => void; popover: ReactNode } {
  // What was asked and its consequences, read when it opened (the detail moves once it is done).
  const [open, setOpen] = useState<{ anchorEl: HTMLElement | null; to: boolean; lines: string[] } | null>(null);
  const mutation = useSetItineraryPublished(detail.id);
  const to = !detail.published;
  const ask = (anchorEl: HTMLElement | null) => setOpen({ anchorEl, to, lines: publishConsequences(detail, to) });
  const item: RecordActionItem = {
    key: to ? ITINERARY_ACTION.publishNow : ITINERARY_ACTION.unpublishNow,
    label: to ? PUBLISH_NOW : UNPUBLISH_NOW,
    immediate: true,
    ...(blockedReason ? { disabled: true, description: blockedReason } : { hint: 'saves at once', onSelect: ask }),
  };
  // Mounted per opening: a closed popover forgets its outcome.
  const popover = open ? (
    <ItineraryNowPopover
      anchorEl={open.anchorEl}
      onClose={() => setOpen(null)}
      title={open.to ? PUBLISH_NOW : UNPUBLISH_NOW}
      subject={itineraryTitle(detail)}
      lines={open.lines}
      confirmLabel={open.to ? PUBLISH_NOW : UNPUBLISH_NOW}
      run={() => mutation.mutateAsync({ published: open.to })}
      receipt={publishReceipt}
      refusal={(e) => publishRefusal(e, open.to)}
      dataKey={open.to ? 'publish' : 'unpublish'}
    />
  ) : null;
  return { item, ask, popover };
}
